import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useShellRootContext } from './ShellRoot';
import { getLaunchOptions } from 'src/utils/serviceRouting';
import { MfeOptionsType } from 'src/types/mfeProps';
import { Logger } from 'src/utils/Logger';
import useFlags from 'src/utils/useFlagWrapper';
import { FLAG_KEYS } from 'src/constants/launchDarklyFlagKeys';

interface MfeOptionsContextType {
  isLoading: boolean;
  options: MfeOptionsType;
}

interface MfeOptionsProviderProps {
  children: React.ReactNode;
}

const MfeOptions = createContext({} as MfeOptionsContextType);

export const MfeOptionsProvider = ({ children }: MfeOptionsProviderProps) => {
  const [optionsAndIsLoading, setOptionsAndIsLoading] =
    useState<MfeOptionsContextType>({
      isLoading: true,
      options: {} as MfeOptionsType
    });
  const { properties } = useShellRootContext();
  const featureFlags = useFlags();

  useEffect(() => {
    Logger.log('Application Started');
    Logger.log('Feature Flags:', featureFlags);

    const fetchOptions = async () => {
      const shouldDefaultToSignUp = !!(featureFlags as Record<string, boolean>)[
        FLAG_KEYS.DEFAULT_ONBOARDING_SIGN_UP
      ];

      const mfeOptions = await getLaunchOptions({
        manifestProperties: properties,
        shouldDefaultToSignUp
      });
      Logger.log('Manifest properties:', properties);
      Logger.log('MFE options:', mfeOptions);
      Logger.log(
        `Application Initialized as ${mfeOptions.initializationMethod}`
      );
      setOptionsAndIsLoading({
        options: mfeOptions,
        isLoading: false
      });
    };

    fetchOptions();
  }, [properties, featureFlags]);

  const value = useMemo(
    () => ({
      isLoading: optionsAndIsLoading.isLoading,
      options: optionsAndIsLoading.options
    }),
    [optionsAndIsLoading]
  );

  return <MfeOptions.Provider value={value}>{children}</MfeOptions.Provider>;
};

export const useMfeOptions = () => {
  const context = useContext(MfeOptions);

  if (!context) {
    throw new Error('useMfeOptions must be used within a MfeOptionsProvider');
  }

  return context;
};
